import { create, type StateCreator, type StoreApi } from 'zustand'
import { persist } from 'zustand/middleware'

import {
  type FilterValue,
  type FilterOption,
} from '../components/multi-select/filterTypeConfig'

export type SelectedOptionsState = {
  products: FilterOption[]
  customers: FilterOption[]
  bookings: FilterOption[]
}
const stores: Record<string, StoreApi<FilterState>> = {}

export type FilterState = {
  appliedFilters: FilterValue[]
  selectedOptions: SelectedOptionsState
  clearAppliedFilters: () => void
  setAppliedFilters: (filters: FilterValue[]) => void
  setSelectedOptions: <K extends keyof SelectedOptionsState>(
    key: K,
    options: FilterOption[]
  ) => void
}

const createFilterStore = (id: string) =>
  create<FilterState>(
    persist(
      (set) => ({
        appliedFilters: [],
        selectedOptions: {
          products: [],
          customers: [],
          bookings: [],
        },
        setAppliedFilters: (filters) => set({ appliedFilters: filters }),
        clearAppliedFilters: () => set({ appliedFilters: [] }),
        setSelectedOptions: (key, options) =>
          set((state) => ({
            selectedOptions: {
              ...state.selectedOptions,
              [key]: options,
            },
          })),
      }),
      {
        name: `filters-${id}`,
      }
    ) as StateCreator<FilterState>
  )

export const getFilterStore = (id: string): StoreApi<FilterState> => {
  if (!stores[id]) {
    stores[id] = createFilterStore(id)
  }
  return stores[id]
}

export const useFilterStore = (id: string) => {
  const store = getFilterStore(id)

  const {
    appliedFilters,
    setAppliedFilters,
    clearAppliedFilters,
    selectedOptions,
    setSelectedOptions,
  } = store.getState()

  return {
    appliedFilters,
    setAppliedFilters: (filters: FilterValue[]) => setAppliedFilters(filters),
    clearAppliedFilters,
    selectedOptions,
    setSelectedOptions,
  }
}
